/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import {
  ChangeEvent,
  SubmitEvent,
  SyntheticEvent,
  useEffect,
  useState
} from "react";

import { useTranslation } from "react-i18next";

import { useLoginDataStore } from "@/stores/useLoginDataStore";

import axios from "../../api/axios";
import { ApiResources } from "../../api/resources";
import BannerMessage from "../../components/BannerMessage/BannerMessage";
import { BANNER_MESSAGE_STATUSES } from "../../components/BannerMessage/types/enums";
import Button, {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS
} from "../../components/Button/Button";
import InputField from "../../components/InputField/InputField";
import { ALERT_STATUS } from "../../context/alert/types";
import { useAuth } from "../../context/Auth/AuthProvider";
import {
  EXAMPLE_EMAIL,
  inputFieldMarginBottom,
  USER_INFO_ERROR_KEYS,
  USER_INFO_LENGTHS
} from "../../shared";
import { OtpInputField } from "../../shared/auth/OtpInputField";
import { getEnvironment } from "../../shared/functions/functions";
import { useEmailValidation } from "../../shared/hooks/useEmailValidation";
import useResponse from "../../shared/hooks/useResponse";
import AuthContainer from "./components/AuthContainer";
import BackToSignIn from "./components/BackToSignIn";
import ContactUs from "./components/ContactUs";
import ForgotPassword from "./components/ForgotPassword/ForgotPassword";
import {
  endOfBetaVersionDate,
  endOfReleasePreparation,
  loginFailedMessage
} from "./constants";
import { useGivenTimeInterval, useTwoFactorVerification } from "./hooks";
import useLogin from "./hooks/useLogin";
import usePasswordValidation from "./hooks/usePasswordValidation";
import useTogglePswVisibility from "./hooks/useTogglePswVisibility";
import { INPUT_FIELD, PASSWORD_TYPE } from "./types";

const Login = () => {
  const {
    setIsLoading,
    errorMessageFromServer,
    setErrorMessageFromServer,
    toggle2FaVerification,
    setTwoFaMethod
  } = useAuth();

  const { handleResponse } = useResponse();
  const { t } = useTranslation();

  const startDisableLogin = endOfBetaVersionDate;
  const endDisableLogin = endOfReleasePreparation;

  const { isGivenTimeGap } = useGivenTimeInterval(
    startDisableLogin,
    endDisableLogin
  );

  const { isProductionEnv } = getEnvironment();

  const setIsLoginState = useLoginDataStore(state => state.setIsLoginState);
  const is2FaVerification = useLoginDataStore(state => state.is2FaVerification);
  const setIs2FaVerification = useLoginDataStore(
    state => state.setIs2FaVerification
  );

  const [userName, setUserName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const [token, setToken] = useState<string>("");

  const {
    emailIsValid,
    emailError,
    setEmailErrorKey,
    validateEmail,
    handleEmailInputChange
  } = useEmailValidation(userName);

  const {
    passwordError,
    setPasswordError,
    clearPasswordError,
    validatePasswordLength
  } = usePasswordValidation();

  const { PasswordVisibility, showPassword } = useTogglePswVisibility(
    PASSWORD_TYPE.Current
  );

  const {
    display2FaVerificationScreen,
    storeTokensAndNavigateToHomepage,
    generateFormTitle
  } = useLogin();

  const {
    twoFaError,
    otpCode,
    setTwoFaError,
    handle2FaSubmit,
    handleOtpChange,
    validateOtp,
    setCode
  } = useTwoFactorVerification({
    setIsLoading,
    setIsButtonLoading,
    token,
    userName
  });

  const disableLogin = isGivenTimeGap && isProductionEnv;

  useEffect(() => {
    setIsBannerOpen(errorMessageFromServer);
  }, [errorMessageFromServer, is2FaVerification, isBannerOpen]);

  // Typescript has no way to verify which types of values might be thrown by arbitrary code. So up until recently, the error had to be of type any.
  const handleError = (error: any) => {
    console.log("handleError LOGIN", error); //debug error response
    // Check if failed login related to incorrect email and/or password
    const incorrectLoginData =
      error.response?.data?.message === loginFailedMessage;

    if (incorrectLoginData) {
      setErrorMessageFromServer(true);
    } else {
      handleResponse(
        ALERT_STATUS.Critical,
        error?.response?.data?.message ?? error?.message
      );
    }
  };

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (emailIsValid && !passwordError.password1 && password.length > 0) {
      const payload = {
        userName: userName,
        password: password
      };

      try {
        // set login state to display auth loader on Users/Me request
        setIsLoginState(true);
        setIsLoading(true);
        setIsButtonLoading(true);
        const response = await axios.post(ApiResources.CreateToken, payload);

        const { twoFactorEnabled, method } = response.data;

        twoFactorEnabled && display2FaVerificationScreen(response, setToken);
        setTwoFaMethod(method);

        // set tokens only if twoFA is not enabled
        if (!twoFactorEnabled) {
          storeTokensAndNavigateToHomepage(response);
        }
      } catch (error) {
        if (error) {
          handleError(error);
        }
      } finally {
        setIsLoading(false);
        setIsButtonLoading(false);
      }
    } else {
      handleInvalidInputs();
    }
  };

  const handleInvalidInputs = () => {
    if (!emailIsValid && !emailError) {
      setEmailErrorKey(USER_INFO_ERROR_KEYS.InvalidEmailFormat);
    }

    if (password.length < 1) {
      setPasswordError("password1", USER_INFO_ERROR_KEYS.Required);
    }
  };

  const handleBacktoSignIn = () => {
    toggle2FaVerification(false);
    setTwoFaError(null);
    setTwoFaMethod(null);
    setIs2FaVerification(false);
    setCode("");
    setUserName("");
    setPassword("");
  };

  const handle2FaSubmitClick = (e: SyntheticEvent) => {
    handle2FaSubmit(e, handleBacktoSignIn);
  };

  const setPasswordErrorFunction = (
    key: string,
    params?: Record<string, unknown>
  ) => {
    setPasswordError("password1", key, params);
  };

  const handlePasswordBlur = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    validatePasswordLength(event, setPasswordErrorFunction);
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pswValue = event.target.value;

    setPassword(pswValue);

    validatePasswordLength(event, setPasswordErrorFunction);

    pswValue.length <= USER_INFO_LENGTHS.PswMax &&
      clearPasswordError("password1");
  };

  return (
    <AuthContainer
      formName={generateFormTitle(is2FaVerification, t)}
      secondaryText={
        is2FaVerification ? (
          <BackToSignIn handleClick={handleBacktoSignIn} />
        ) : (
          <ContactUs />
        )
      }
    >
      <form
        noValidate
        onSubmit={is2FaVerification ? handle2FaSubmitClick : handleSubmit}
        data-testid={is2FaVerification ? "two-fa-verification" : "login-form"}
      >
        {isBannerOpen && (
          <BannerMessage
            status={BANNER_MESSAGE_STATUSES.Critical}
            title={t("Auth##failed to login")}
            externalCustomStyle={inputFieldMarginBottom}
          />
        )}

        {is2FaVerification ? (
          <>
            <OtpInputField
              value={otpCode}
              isLoading={isButtonLoading}
              error={twoFaError}
              onChange={handleOtpChange}
              onBlur={validateOtp}
            />

            <Button
              data-testid="verify-button"
              fullWidth
              color={BUTTON_COLORS.Primary}
              size={BUTTON_SIZES.Normal}
              variant={BUTTON_VARIANTS.TextOnly}
              type="submit"
              disabled={Boolean(twoFaError) || otpCode.length < 6}
              isLoading={isButtonLoading}
              css={css({
                marginTop: "16px"
              })}
            >
              {t("Button##verify")}
            </Button>
          </>
        ) : (
          <>
            <InputField
              key={`${disableLogin}-email`}
              data-testid="email-field"
              size="medium"
              id="standard-error-helper-text"
              fullWidth
              name={INPUT_FIELD.Email}
              onBlur={validateEmail}
              errorText={emailIsValid ? "" : emailError}
              onChange={e => handleEmailInputChange(e, setUserName)}
              labelLeft={t("Auth##email")}
              placeholder={`${t("Auth##example")} ${EXAMPLE_EMAIL}`}
              customStyle={{ marginBottom: "16px" }}
              value={userName}
              disabled={disableLogin}
            />
            <InputField
              key={`${disableLogin}-password`}
              data-testid="password-field"
              fullWidth
              value={password}
              name={INPUT_FIELD.Password}
              labelLeft={t("Auth##password")}
              placeholder={t("Auth##enter here")}
              size="medium"
              type={showPassword.current ? "text" : "password"}
              onBlur={handlePasswordBlur}
              onChange={handlePasswordChange}
              errorText={passwordError.password1}
              iconRight={<PasswordVisibility />}
              disabled={disableLogin}
            />
            <ForgotPassword isDisabled={disableLogin} />
            <Button
              data-testid="sign-in-button"
              fullWidth
              variant="textOnly"
              size="normal"
              color="primary"
              type="submit"
              isLoading={isButtonLoading}
              disabled={disableLogin}
            >
              {t("Auth##sign in")}
            </Button>
          </>
        )}
      </form>
    </AuthContainer>
  );
};

export default Login;
